import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import { getPost, getAllPostPathComponents } from 'lib/api/posts';
import ResistCTAButtons from 'components/buttons/cta';
import Layout, { DATA_TYPES } from 'components/layout';
import PortableText from 'components/portableText';
import Heading from 'components/typography/heading';
import Text from 'components/typography/text';
import { toPlainText } from 'utilities/blockText';
import { ShareBlock } from 'components/petition/shareblock';
import { BlogPostDetailType } from 'utilities/propTypes';

import {
  blogPost,
  postDate,
  description,
  author,
  postBody,
  ctaBlock,
} from './blog-post.module.scss';

const NUM_BLOGS_TO_RENDER = 3;

export const getStaticProps = async ({ params, preview = false }) => {
  const post = await getPost({
    year: params.year,
    month: params.month,
    day: params.day,
    slug: params.slug,
    preview,
  });
  if (!post) {
    return {
      notFound: true,
    };
  } else {
    return {
      props: {
        preview,
        post: post,
      },
    };
  }
};

export const getStaticPaths = async () => {
  const paths = await getAllPostPathComponents();
  return {
    paths: paths.slice(0, NUM_BLOGS_TO_RENDER),
    fallback: 'blocking',
  };
};

const UPDATED_MIN_TIME_HOURS = 12; // Give a little buffer in case we make small edits after publishing.
const BlockText = ({ blocks }) => <PortableText blocks={blocks} />;

const BlogPostDetail = ({ post }) => {
  const router = useRouter();
  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />;
  }

  if (router.isFallback) {
    return <div>Loading...</div>;
  }

  const publishedAtDate = DateTime.fromISO(post.publishedAt);
  const lastUpdatedDate = DateTime.fromISO(post.lastUpdated);
  const wasUpdated =
    lastUpdatedDate.diff(publishedAtDate, 'hours') > UPDATED_MIN_TIME_HOURS;

  const createdDateFormatted = publishedAtDate.toLocaleString(
    DateTime.DATE_FULL
  );
  const lastUpdatedFormatted = lastUpdatedDate.toLocaleString(
    DateTime.DATE_FULL
  );

  const descriptionPlainText = toPlainText(post.description);

  return (
    <Layout
      title={post.title}
      dataType={DATA_TYPES.blog}
      description={descriptionPlainText}
      keywords={post.tags}
      image={post.mainImage.asset.url}
      imageAltText={post.mainImage.alt}
      publishedOn={post.publishedAt}
      updatedOn={wasUpdated ? post.lastUpdated : null}
    >
      <div className={blogPost}>
        <Text as="span" className={postDate}>
          Published {createdDateFormatted}
        </Text>
        {wasUpdated ? (
          <Text as="span" className={postDate}>
            &nbsp;/ Updated {lastUpdatedFormatted}
          </Text>
        ) : null}
        <Heading as="h1" size="h1">
          {post.title}
        </Heading>
        <div className={description}>
          <BlockText blocks={post.description} />
        </div>
        {post.authors && (
          <Text className={author}>
            <em>by</em> {post.authors.map((a) => a.name).join(', ')}
          </Text>
        )}
        <ShareBlock pathname={router.asPath} text={post.title} />
        <div className={postBody}>
          <BlockText blocks={post.body} />
        </div>
      </div>
      <div className={ctaBlock}>
        <ResistCTAButtons keyword={post.featuredKeyword} />
      </div>
    </Layout>
  );
};

export default BlogPostDetail;

BlockText.propTypes = {
  blocks: PropTypes.arrayOf(PropTypes.shape({})),
};

BlogPostDetail.propTypes = BlogPostDetailType;
